import { useEffect, useState, useRef, RefObject } from 'react';
import { keyframes } from '@mui/system';

// Hook to detect if user prefers reduced motion
export const usePrefersReducedMotion = (): boolean => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return prefersReducedMotion;
};

// Hook to detect when element enters viewport
export const useInView = (
  options: IntersectionObserverInit = { threshold: 0.1 }
): [RefObject<HTMLDivElement>, boolean] => {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [options]);

  return [ref, isInView];
};

// Hook for staggered animations
export const useStaggeredAnimation = (
  isInView: boolean,
  index: number,
  baseDelay: number = 0
) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);

  useEffect(() => {
    if (isInView) {
      const timer = setTimeout(() => {
        setShouldAnimate(true);
      }, baseDelay + index * 80);

      return () => clearTimeout(timer);
    }
  }, [isInView, index, baseDelay]);

  return shouldAnimate;
};

// Animation keyframes
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const neonGlow = keyframes`
  0%, 100% {
    box-shadow: 0 0 10px rgba(10, 124, 160, 0.3),
                0 0 20px rgba(10, 124, 160, 0.1),
                inset 0 0 10px rgba(10, 124, 160, 0.05);
  }
  50% {
    box-shadow: 0 0 20px rgba(10, 124, 160, 0.5),
                0 0 40px rgba(10, 124, 160, 0.2),
                inset 0 0 15px rgba(10, 124, 160, 0.1);
  }
`;

export const spin = keyframes`
  from {
    transform: rotateY(0deg);
  }
  to {
    transform: rotateY(360deg);
  }
`;

export const typewriter = keyframes`
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
`;

export const blink = keyframes`
  0%, 50% {
    opacity: 1;
  }
  51%, 100% {
    opacity: 0;
  }
`;

export const fillGauge = keyframes`
  from {
    stroke-dashoffset: 440;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

export const barRise = keyframes`
  from {
    height: 0;
  }
  to {
    height: var(--bar-height);
  }
`;

export const sparkle = keyframes`
  0% {
    opacity: 0;
    transform: scale(0) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1) rotate(180deg);
  }
  100% {
    opacity: 0;
    transform: scale(0) rotate(360deg);
  }
`;

export const driftUp = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-20px) scale(0.8);
  }
`;

// Easing functions
export const easings = {
  outQuint: 'cubic-bezier(0.22, 1, 0.36, 1)',
  spring: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
  smooth: 'cubic-bezier(0.4, 0, 0.2, 1)',
};

// Get animation style based on reduced motion preference
export const getAnimationStyle = (
  animation: string,
  prefersReducedMotion: boolean,
  fallback?: Record<string, any>
) => {
  if (prefersReducedMotion) {
    return fallback || { transition: 'none' };
  }
  return { animation };
};

// Format animation duration for consistent timing
export const getAnimationDuration = (
  base: number,
  prefersReducedMotion: boolean
): string => {
  if (prefersReducedMotion) return '0ms';
  return `${base}ms`;
};